import { ApiError } from "../api.js";
import { esc } from "../utils.js";
import { planCardHtml, attachPlanCardHandlers } from "../divePlanCard.js";
import { infiniteScroll } from "../infiniteScroll.js";
import { navigate } from "../app.js";

/**
 * Dive Events (#/dive-plans): one list, as the Following feed is, of the
 * events of what you follow (?feed=following): organized or joined by divers
 * you follow, in your cohorts (unless muted), at dive sites you follow, and
 * those you've joined (unless muted).
 *
 * Upcoming first, soonest at the top, then the past ones under "Past", most
 * recent first: two paged lists read one after the other, the past ones
 * starting once there are no more upcoming.
 */

function emptyStateHtml(icon, title, subtitle) {
  return `<div class="dc-empty"><i class="bi ${icon}"></i><div class="fw-semibold">${esc(title)}</div><div>${esc(subtitle)}</div></div>`;
}

/** The scrollers on screen, so a new render can stop the old ones watching. */
let active = [];

function stopAll() {
  for (const feed of active) feed.destroy();
  active = [];
}

function renderPlans(list, plans) {
  const batch = document.createElement("div");
  batch.innerHTML = plans.map(planCardHtml).join("");
  attachPlanCardHandlers(batch);
  list.append(batch);
}

export function render(container) {
  stopAll();
  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="display-heading h3 mb-0">Dive Events</h1>
      <button class="btn btn-accent" id="new-plan-btn"><i class="bi bi-plus-lg me-1"></i>Plan a Dive</button>
    </div>
    <div id="plan-upcoming"></div>
    <h2 class="h6 text-slate text-uppercase mt-4 mb-3 d-none" id="plan-past-heading">Past</h2>
    <div id="plan-past"></div>`;

  container.querySelector("#new-plan-btn").addEventListener("click", () => navigate("/create-dive-plan"));

  const upcomingHost = container.querySelector("#plan-upcoming");
  const pastHost = container.querySelector("#plan-past");
  const pastHeading = container.querySelector("#plan-past-heading");

  const onError = (host) => (err) => {
    const isAuthError = err instanceof ApiError && err.status === 401;
    host.innerHTML = isAuthError
      ? emptyStateHtml("bi-lock", "Sign in required", "Log in to see the dive events of what you follow.")
      : emptyStateHtml("bi-exclamation-triangle", "Could not load dive events", err.message || "");
  };

  function startPast(upcomingCount) {
    // Nothing upcoming and nothing past: the whole page says what to follow.
    const nothingAtAll = emptyStateHtml(
      "bi-calendar-event",
      "No dive events yet",
      "Follow divers and dive sites, or join cohorts and dive events, to see their dive events here."
    );
    active.push(infiniteScroll(pastHost, {
      path: "/dive-plans",
      query: { feed: "following", when: "past" },
      emptyHtml: upcomingCount === 0 ? nothingAtAll : "",
      onError: onError(pastHost),
      render: (list, plans) => {
        pastHeading.classList.remove("d-none");
        renderPlans(list, plans);
      },
      // Nothing at all: the page's empty state alone, not "No upcoming" above it.
      onDone: (pastCount) => {
        if (upcomingCount === 0 && pastCount === 0) upcomingHost.innerHTML = "";
      },
    }));
  }

  active.push(infiniteScroll(upcomingHost, {
    path: "/dive-plans",
    query: { feed: "following", when: "upcoming" },
    // Kept when there are past ones; cleared when there's nothing at all.
    emptyHtml: `<p class="text-slate mb-0" id="no-upcoming">No upcoming dive events.</p>`,
    onError: onError(upcomingHost),
    render: renderPlans,
    onDone: startPast,
  }));
}
